import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import TutorialMenu from './TutorialMenu';
import TutorialRunner from './TutorialRunner';
import TutorialAutoOpen from './TutorialAutoOpen';
import useTutorialProgress from './useTutorialProgress';
import useTutorialLifecycle from './useTutorialLifecycle';
import useUserProfileHydration from './useUserProfileHydration';
import { findTutorial } from './tutorialSteps';
import { readStoredUserEmail } from './tutorialProgress';
import {
  clearPendingTutorial as clearPendingTutorialAction,
  closeTutorialMenu as closeTutorialMenuAction,
  finishTutorial as finishTutorialAction,
  markAutoOpenEvaluated as markAutoOpenEvaluatedAction,
  openTutorialMenu as openTutorialMenuAction,
  startTutorial as startTutorialAction,
  stopTutorial as stopTutorialAction,
} from '../../actions/tutorial';
import {
  getUserInfo as getUserInfoAction,
  updateUserDetails as updateUserDetailsAction,
} from '../../actions/userProfile';

/**
 * Owns "Learn Medsender": the checklist, the tour runner, per-user progress and
 * the `has_completed_tutorial` summary flag.
 *
 * Mounted once, app-wide, so the help-menu entry works from any screen and the
 * post-walkthrough auto-open can fire wherever the walkthrough dropped the user.
 *
 * The behaviour lives in two colocated hooks so this file stays readable:
 * `useTutorialProgress` (what the user has completed, and the durable flag) and
 * `useTutorialLifecycle` (launching, ending and resuming a running tour).
 */
const LearnMedsender = ({
  menuOpen,
  activeTutorialId,
  pendingTutorialId,
  autoOpenEvaluated,
  userEmail,
  userProfileLoaded,
  userProfileSettled,
  userProfileLoading,
  hasCompletedWalkthrough,
  hasCompletedTutorial,
  walkthroughStartDestination,
  hasEmrIntegration,
  impersonating,
  history,
  location,
  getUserInfo: getUserInfoProp,
  clearPendingTutorial: clearPendingTutorialProp,
  closeTutorialMenu: closeTutorialMenuProp,
  finishTutorial: finishTutorialProp,
  markAutoOpenEvaluated: markAutoOpenEvaluatedProp,
  openTutorialMenu: openTutorialMenuProp,
  startTutorial: startTutorialProp,
  stopTutorial: stopTutorialProp,
  updateUserDetails: updateUserDetailsProp,
}) => {
  // Nothing else in the app guarantees GET /api/v1/users has run, and this
  // component owns the flag it carries. See useUserProfileHydration.
  useUserProfileHydration({
    userEmail,
    impersonating,
    // SETTLED, not succeeded: a failed fetch must not be retried on a loop, and
    // the consumers below already refuse to act on one.
    userInfoLoaded: userProfileSettled,
    userInfoLoading: userProfileLoading,
    getUserInfo: getUserInfoProp,
  });

  const {
    progress,
    tutorials,
    coreTutorials,
    completedCount,
    completeTutorial,
    beginTutorialRun,
    dismissTutorials,
  } = useTutorialProgress({
    userEmail,
    hasEmrIntegration,
    hasCompletedTutorial,
    impersonating,
    updateUserDetails: updateUserDetailsProp,
    finishTutorial: finishTutorialProp,
    closeTutorialMenu: closeTutorialMenuProp,
  });

  const activeTutorial = findTutorial(tutorials, activeTutorialId);

  const {
    start,
    exit,
    reportUnavailableStep,
    reportBlockedStep,
  } = useTutorialLifecycle({
    tutorials,
    activeTutorial,
    activeTutorialId,
    pendingTutorialId,
    progress,
    impersonating,
    history,
    location,
    completeTutorial,
    beginTutorialRun,
    clearPendingTutorial: clearPendingTutorialProp,
    openTutorialMenu: openTutorialMenuProp,
    startTutorial: startTutorialProp,
    stopTutorial: stopTutorialProp,
  });

  if (impersonating) return null;

  return (
    <>
      <TutorialAutoOpen
        autoOpenEvaluated={autoOpenEvaluated}
        markAutoOpenEvaluated={markAutoOpenEvaluatedProp}
        openTutorialMenu={openTutorialMenuProp}
        impersonating={impersonating}
        userProfileLoaded={userProfileLoaded}
        userEmail={userEmail}
        hasCompletedWalkthrough={hasCompletedWalkthrough}
        hasCompletedTutorial={hasCompletedTutorial}
        walkthroughStartDestination={walkthroughStartDestination}
        progress={progress}
        tutorials={coreTutorials}
      />
      {menuOpen && (
        <div className="tutorial-menu-layer">
          <TutorialMenu
            tutorials={tutorials}
            completedCount={completedCount}
            onStart={start}
            onClose={() => dismissTutorials('close_button')}
            onDismiss={() => dismissTutorials('dont_show_again')}
          />
        </div>
      )}
      {activeTutorial && (
        <TutorialRunner
          tutorial={activeTutorial}
          onComplete={completeTutorial}
          onExit={exit}
          onUnavailable={reportUnavailableStep}
          onBlocked={reportBlockedStep}
        />
      )}
    </>
  );
};

LearnMedsender.defaultProps = {
  menuOpen: false,
  activeTutorialId: null,
  pendingTutorialId: null,
  autoOpenEvaluated: false,
  userEmail: '',
  userProfileLoaded: false,
  userProfileSettled: false,
  userProfileLoading: false,
  hasCompletedWalkthrough: false,
  hasCompletedTutorial: false,
  walkthroughStartDestination: null,
  hasEmrIntegration: false,
  impersonating: false,
};

LearnMedsender.propTypes = {
  menuOpen: PropTypes.bool,
  activeTutorialId: PropTypes.string,
  pendingTutorialId: PropTypes.string,
  autoOpenEvaluated: PropTypes.bool,
  userEmail: PropTypes.string,
  userProfileLoaded: PropTypes.bool,
  userProfileSettled: PropTypes.bool,
  userProfileLoading: PropTypes.bool,
  hasCompletedWalkthrough: PropTypes.bool,
  hasCompletedTutorial: PropTypes.bool,
  walkthroughStartDestination: PropTypes.string,
  hasEmrIntegration: PropTypes.bool,
  impersonating: PropTypes.bool,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  updateUserDetails: PropTypes.func.isRequired,
  clearPendingTutorial: PropTypes.func.isRequired,
  markAutoOpenEvaluated: PropTypes.func.isRequired,
  openTutorialMenu: PropTypes.func.isRequired,
  closeTutorialMenu: PropTypes.func.isRequired,
  startTutorial: PropTypes.func.isRequired,
  stopTutorial: PropTypes.func.isRequired,
  finishTutorial: PropTypes.func.isRequired,
};

export { LearnMedsender as UnconnectedLearnMedsender };

// Every action this feature dispatches goes through a bound prop. The raw
// `dispatch` prop is deliberately NOT exposed: it existed only so TutorialAutoOpen
// could dispatch two actions itself, which meant one feature had two dispatch
// mechanisms and the tests could only observe one of them.
const mapDispatchToProps = (dispatch) => ({
  getUserInfo: (...args) => dispatch(getUserInfoAction(...args)),
  updateUserDetails: (...args) => dispatch(updateUserDetailsAction(...args)),
  openTutorialMenu: (...args) => dispatch(openTutorialMenuAction(...args)),
  closeTutorialMenu: (...args) => dispatch(closeTutorialMenuAction(...args)),
  startTutorial: (...args) => dispatch(startTutorialAction(...args)),
  stopTutorial: (...args) => dispatch(stopTutorialAction(...args)),
  finishTutorial: (...args) => dispatch(finishTutorialAction(...args)),
  clearPendingTutorial: (...args) => dispatch(clearPendingTutorialAction(...args)),
  markAutoOpenEvaluated: (...args) => dispatch(markAutoOpenEvaluatedAction(...args)),
});

/**
 * Exported so it can be tested directly.
 *
 * `__tests__/setupTests.js` stubs react-redux's `connect` as a pass-through, so an
 * inline selector here is invisible to the entire Jest suite — a `mapStateToProps`
 * that throws passes every test. That is not hypothetical: the unguarded
 * `state.auth.data.autoUploadEmr` below survived a 3989-test run and was caught in
 * review. Exporting it means `LearnMedsender.test.jsx` can run it against the real
 * reducers, including the `auth.data === null` shape.
 */
export const mapStateToProps = (state) => ({
  menuOpen: state.tutorial.menuOpen,
  activeTutorialId: state.tutorial.activeTutorialId,
  pendingTutorialId: state.tutorial.pendingTutorialId,
  autoOpenEvaluated: state.tutorial.autoOpenEvaluated,
  // `m8_uid` FIRST, deliberately.
  //
  // This used to prefer `state.userProfile.email`. That field IS assigned — by
  // FINISHED_GET_USER_INFO's spread — and the reducer had no logout reset, so
  // after an in-app logout it held the PREVIOUS account's address while `m8_uid`
  // (which every login path rewrites and logout clears) already held the current
  // one. Preferring the reducer therefore namespaced the new user's progress under
  // the old user's email — the leak that `PRESERVED_LEARNING_KEYS` then made
  // durable. The reducer now resets at the auth boundary too, so both agree; the
  // order here is the belt to that braces.
  userEmail: readStoredUserEmail() || state.userProfile.email,
  // A REAL fetch-landed signal, not "an email exists somewhere". See
  // reducers/userProfile.js.
  //
  // `userProfileLoaded` is deliberately the strict one — SUCCEEDED, not merely
  // finished. A failed GET settles too, and leaves `has_completed_tutorial` at
  // its default `false`, so a consumer that spends a one-shot on it (the
  // auto-open latch) would re-prompt a user the server has marked done because
  // of one transient 500.
  //
  // `userProfileSettled`/`userProfileLoading` answer the different question
  // useUserProfileHydration asks — "does anyone still owe this fetch?" — where a
  // failure counts as an answer and must not be retried on a loop.
  userProfileLoaded: !!state.userProfile.userInfoLoaded && !state.userProfile.userInfoLoadFailed,
  userProfileSettled: !!state.userProfile.userInfoLoaded,
  userProfileLoading: !!state.userProfile.userInfoLoading,
  hasCompletedWalkthrough: !!state.auth.hasCompletedWalkthrough,
  hasCompletedTutorial: !!state.userProfile.has_completed_tutorial,
  // The walkthrough's final "what would you like to do first?" answer. A user who
  // picked Home has asked NOT to be handed the checklist; see
  // shouldAutoOpenTutorialMenu. Read from userProfile rather than auth because
  // that is where both FINISHED_UPDATE_USER_DETAILS (completing the walkthrough
  // in-session) and FINISHED_GET_USER_INFO (a later page load) write it.
  walkthroughStartDestination: state.userProfile.walkthrough_start_destination || null,
  // Either signal means chart actions are reachable for this org. `auth.data` is
  // `null` before login and again after LOGGED_OUT (reducers/auth.js resets to
  // `defaultState`), and this container is mounted app-wide, so an unguarded
  // dereference here throws on every state change in those windows and takes the
  // subtree down with it.
  hasEmrIntegration: !!state.auth.hasEmrIntegrationCredential || !!state.auth.data?.autoUploadEmr,
  impersonating: !!state.auth.impersonating,
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LearnMedsender));
